/**
 * EN-11109: the engine's registration refusals that mean "this table is no
 * longer taking registrations": it filled up (and started) between the
 * player's tap and the engine reading it.
 *
 * Mirrors apps/engine/languages/en.js (and fr.js, which keeps the same English
 * text, and serverless-v2/shared/languages/en.js):
 *  - TOURNAMENT_REGISTRATION_EXPIRED: a Rolling Sit & Go whose registered
 *    count has reached maxPlayers (messages/inbound.js, case 'register').
 *  - TOURNAMENT_REGISTRATION_FULL: registeredPlayers >= maxPlayers on the
 *    capacity check that follows.
 * Both are sent before the engine reads the balance, takes the registration
 * lock or writes anything, so nothing was charged and registering elsewhere
 * cannot double-charge.
 *
 * Both carry the title constants.RESPONSE_MSG.REGISTRATION_FAILED. Every other
 * refusal (insufficient chips, maintenance, a platform integrity block) keeps
 * its own handling, except "already registered" (below).
 *
 * TOURNAMENT_ALREADY_REGISTERED, under the same failure title, is not a
 * refusal at all for a rematch: the player IS registered and was charged. It
 * answers a Confirm sent again after the first registration took but its reply
 * was lost and the player's details had not caught up yet.
 */

export const ENGINE_REGISTRATION_FAILED_TITLE = 'Registration Failed';

export const ENGINE_REGISTRATION_CLOSED_MESSAGES = [
  'Sorry, The registration time is expired!',
  'Sorry, This tournament is full.'
] as const;

/**
 * What the rematch shows when it gives up looking for a table that takes the
 * registration. Nothing was charged: every refusal above precedes the debit.
 */
export const NO_OPEN_TABLE_NOTICE = {
  title: 'No open table',
  message:
    'Every table filled up before you could join. Nothing was charged. Pick a table in the lobby.'
} as const;

const normalize = (text: unknown): string =>
  typeof text === 'string' ? text.trim().replace(/\s+/g, ' ').toLowerCase() : '';

const CLOSED = new Set(ENGINE_REGISTRATION_CLOSED_MESSAGES.map(normalize));

/**
 * True for a gotTnmRegister reply refusing the registration because the table
 * no longer takes registrations. Matched on the whole engine message (case and
 * spacing aside) under the engine's failure title, never on a fragment, so a
 * reworded or unrelated refusal is never mistaken for one.
 */
export const isRegistrationClosedRefusal = (title: unknown, msg: unknown): boolean =>
  normalize(title) === normalize(ENGINE_REGISTRATION_FAILED_TITLE) && CLOSED.has(normalize(msg));

export const ENGINE_ALREADY_REGISTERED_MESSAGE = 'You have already registered in this tournament.';

/**
 * True for a gotTnmRegister reply saying the player is already registered,
 * matched the same way: the whole engine message under its failure title.
 */
export const isAlreadyRegisteredReply = (title: unknown, msg: unknown): boolean =>
  normalize(title) === normalize(ENGINE_REGISTRATION_FAILED_TITLE) &&
  normalize(msg) === normalize(ENGINE_ALREADY_REGISTERED_MESSAGE);
